import { useRef, useState } from "react";
import { Form, Link, redirect, useActionData, useNavigation, useParams } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formError } from "@/lib/session";
import { draftPart, readNewEvent, readParts } from "@/lib/event-form";
import { applyEventImage, loadSpaceContext, useSpaceContext } from "@/lib/events";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { tenantPath } from "@/lib/tenants";
import { viewerZone } from "@/lib/times";
import { ErrorNote, ProblemProvider, useFormProblem } from "@/components/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DraftParts, EventFields, NotOnAPost, PartSummary, boundsOf, type DraftPart } from "@/components/event-fields";
import { EventImageField } from "@/components/event-image";
import { LeaveGuard } from "@/components/leave-guard";
import { BackLink } from "@/components/navigation";
import { PageTitle } from "@/components/brand";

/**
 * Posting an event, with its parts and, where the deployment keeps pictures,
 * its image. For the owner and admins holding manage_events; anybody else is
 * told so rather than shown a form the api would refuse.
 *
 * Each part is made in its sheet, as it is on the event's page, and waits
 * here as a card until Create event sends it with the rest (decided with
 * Tiago 20 October). Leaving with anything typed asks first.
 */

export async function newEventLoader({ request, params }: LoaderFunctionArgs) {
  const context = await loadSpaceContext(request, params.slug ?? "");
  if (context.canManageEvents) await read(queries.eventOptions());
  return null;
}

export async function newEventAction({ request, params }: ActionFunctionArgs) {
  const form = await request.formData();
  const slug = params.slug ?? "";
  try {
    // Read again rather than carried from the loader: the zone and the lists
    // are what they are when the form is sent.
    const [profile, options] = await Promise.all([api.me.profile.get(), api.eventOptions()]);
    const read = readNewEvent(form, viewerZone(profile.timeZone).zone, options);
    if (read.error !== undefined) return { error: read.error, field: read.field };
    const event = await api.space(slug).events.create(read.value);
    // The image belongs to an event that exists, so it goes up second. The
    // event is posted whatever becomes of it, and its edit form is where to
    // try the image again — not this one, which would post it twice.
    try {
      await applyEventImage(slug, event.id, form);
    } catch {
      return redirect(`/t/${slug}/events/${event.id}/edit?image=failed`);
    }
    return redirect(`/t/${slug}/events/${event.id}`);
  } catch (error) {
    // The field the api names — the deadline, or a part's start or deadline — is shown beside it.
    return formError(error, "Could not post the event");
  }
}

export default function NewEvent() {
  const { session, tenant, canManageEvents, zone } = useSpaceContext(useParams().slug ?? "");
  // Read by the loader for those who may post; nobody else asks.
  const options = useQuery({ ...queries.eventOptions(), enabled: canManageEvents }).data ?? null;
  const result = useActionData() as { error?: string; field?: string } | undefined;
  const navigation = useNavigation();
  const base = tenantPath(tenant);
  const [parts, setParts] = useState<DraftPart[]>([]);
  // Post has no parts (0090): their cards wait, hidden, for a switch back.
  const [post, setPost] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  // The event's end as it last came to, so the parts that took it follow it.
  const deadline = useRef("");
  const shown = useFormProblem(result, form);

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <BackLink to={base}>{tenant.name}</BackLink>
        <PageTitle of={["New event", tenant.name]} />
        <h1 className="page-title">New event in {tenant.name}</h1>
      </div>

      {!canManageEvents || !options ? (
        <Alert role="note">
          <AlertDescription>
            {tenant.isPersonal
              ? "A personal space holds your own results, not events. Post events in a space you share."
              : "Only the owner, and admins the owner has given Manage events, can post events here."}
          </AlertDescription>
        </Alert>
      ) : (
        <Form
          ref={form}
          method="post"
          encType={session.images ? "multipart/form-data" : undefined}
          className="space-y-6"
          // The app says what is missing, beside its box, rather than the browser.
          noValidate
          onChange={shown.onChange}
        >
          {shown.general && <ErrorNote message={shown.general} />}
          <Card>
            <CardContent>
              <ProblemProvider value={shown.problem}>
                <EventFields
                  options={options}
                  zone={zone}
                  image={session.images ? <EventImageField current={null} /> : undefined}
                  onPost={setPost}
                  onDeadline={(next) => {
                    // A part that took the event's deadline keeps following it; one given its own
                    // stays as it is (0083). The end is never taken away (0088): none is only a
                    // box half typed, so the parts wait for the next end rather than letting go.
                    if (!next) return;
                    const before = deadline.current;
                    deadline.current = next;
                    if (!before || before === next) return;
                    setParts((kept) =>
                      kept.map((part) =>
                        part.fields.some(([name, value]) => name === "deadline" && value === before)
                          ? {
                              ...part,
                              fields: part.fields.map(([name, value]) =>
                                name === "deadline" && value === before ? [name, next] : [name, value],
                              ),
                            }
                          : part,
                      ),
                    );
                  }}
                />
              </ProblemProvider>
            </CardContent>
          </Card>
          {/* None until somebody adds one, and none on a post (0090). */}
          <NotOnAPost post={post}>
            <ProblemProvider value={shown.problem}>
              <DraftParts
                parts={parts}
                onChange={setParts}
                options={options}
                check={(sheet, index) => readParts(sheet, zone, options, index)}
                // A new part starts due on the event's deadline, as the form has it then.
                bounds={() => boundsOf(form.current, zone)}
                describe={(fields, index) => {
                  const part = draftPart(fields, zone, options);
                  return part && <PartSummary part={part} index={index} zone={zone} />;
                }}
              />
            </ProblemProvider>
          </NotOnAPost>
          {/* No toast once it is posted: the event's own page opening says so. */}
          <div className="flex justify-end gap-2">
            <Button asChild variant="outline" size="lg">
              <Link to={base}>Cancel</Link>
            </Button>
            <Button type="submit" size="lg" disabled={navigation.state === "submitting"}>
              Create event
            </Button>
          </div>
          <LeaveGuard form={form} title="Leave without creating the event?">
            What you typed here is not saved.
          </LeaveGuard>
        </Form>
      )}
    </section>
  );
}
